import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { loadApplicantAutomations } from '../../applicant-automations/actions/load-applicant-automations';
import { applicantAutomationsForOrgIdSelector } from '../../applicant-automations/applicant-automation-selectors';
import { loadMergeIntegration } from '../../merge-integration/actions/load-merge-integration';
import { loadWebhookEventSubscriptions } from '../actions/load-webhook-event-subscriptions';
import {
  webhookEventSubscriptionsForOrgId,
  webhookEventSubscriptionsStateForOrgId,
} from '../webhook-event-subscription-selectors';

import { UiButton } from '../../ui/ui-button';
import { orgAutomationsRoute } from '../../web-routes';
import { WebhookEventSubscriptionDialog } from '../webhook-event-subscription-dialog';
import { WebhookEventTestButton } from '../webhook-event-test-button';

import { SyncWithInova } from './sync-with-inova';

import { inovaWriteDescription } from './utils/inova-component-utils';
import { ReactComponent as InovaLogo } from './utils/inova-logo.svg';
import { APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES } from '../../automations/utils/applicant-automation-communication-types';
import { WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE } from '../utils/webhook-event-subscription-utils';

type InputProps = {
  org: Org;
};

export const ConnectInovaIntegration = (props: InputProps) => {
  const { org } = props;
  const orgId = org.org_id;

  const history = useHistory();
  const dispatch = useDispatch();

  const webhookSubscriptions = useSelector((state: any) =>
    webhookEventSubscriptionsForOrgId(state, orgId),
  );

  const webhookSubscriptionState = useSelector((state: any) =>
    webhookEventSubscriptionsStateForOrgId(state, orgId),
  );
  const applicantAutomations = useSelector((state: any) =>
    applicantAutomationsForOrgIdSelector(state, orgId),
  );

  const existingSubscription =
    webhookSubscriptions?.find(
      (sub) => sub.subscriberType === WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE.INOVA,
    ) || null;

  useEffect(() => {
    dispatch(loadWebhookEventSubscriptions(orgId));
    dispatch(loadMergeIntegration(orgId));
    if (existingSubscription) {
      dispatch(loadApplicantAutomations({ orgId }));
    }
  });

  const [isEditing, setIsEditing] = useState<boolean>(false);

  const handleToggleIsEditing = () => {
    setIsEditing(!isEditing);
  };

  const inovaAutomation = applicantAutomations?.find(
    (a) => a.displayCondition === APPLICANT_AUTOMATION_DISPLAY_CONDITION_TYPES.INOVA_SUBSCRIPTION,
  );
  const handleRouteToAutomation = () => {
    if (!inovaAutomation) {
      return;
    }

    history.push(
      orgAutomationsRoute(orgId, {
        focusApplicantAutomationId: inovaAutomation.applicantTriggeredCommunicationId,
      }),
    );
  };

  return (
    <div>
      <div className="typo-subtitle u-maxWidth-750px u-margin-bottom-lg">
        {inovaWriteDescription}
      </div>
      <div className="u-display-flex u-justifyContent-spaceBetween">
        <UiButton
          className="u-background-inova u-color-black"
          size="sm"
          onClick={handleToggleIsEditing}
          loading={webhookSubscriptionState?.loading}
        >
          <InovaLogo width={30} height={30} className="u-margin-right" />
          {existingSubscription ? 'Manage Inova Connection' : 'Connect To Inova'}
        </UiButton>
        <div>
          {existingSubscription && inovaAutomation ? (
            <UiButton
              className="u-margin-right-md"
              size="xs"
              variant="tertiary"
              onClick={handleRouteToAutomation}
            >
              View Automation
            </UiButton>
          ) : null}
          <WebhookEventTestButton webhookEventSubscription={existingSubscription} />
        </div>
      </div>
      <SyncWithInova org={org} />
      {isEditing && (
        <WebhookEventSubscriptionDialog
          org={org}
          subscriberType={WEBHOOK_EVENT_SUBSCRIPTION_SUBSCRIBER_TYPE.INOVA}
          existingSubscription={existingSubscription}
          onClose={handleToggleIsEditing}
        />
      )}
    </div>
  );
};
